import { useEffect, useState } from "react"

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Single rAF-throttled, passive scroll listener. Returns the scroll offset and
// 0..1 page progress.
//
// ONE consumer, deliberately: components/ScrollProgressBar.jsx, which carries
// the full reasoning. In short: a new state object every animation frame is
// cheap in a one-div component and ruinous at a page root, where it re-renders
// the whole tree. It used to be called at the funnel page root and fed the
// progress bar, the nav shadow and a hero parallax; the parallax went with the
// Establishment restyle, the nav shadow moved to an IntersectionObserver
// sentinel in Landing.jsx, and the hook moved into the bar (act:aebf07fa).
//
// Call this only from a component small enough that re-rendering it once per
// frame is free, never from a page root.
// `spec/contracts/scroll_hook_consumer_spec.rb` fails if a second importer
// appears — read its header before adding one.
//
// `prefersReducedMotion` above is a SEPARATE export with its own importers
// (Landing.jsx, Admin/LegalTheories/Form.jsx), so the one-consumer rule does not
// apply to it — which is why this file survives rather than collapsing into
// ScrollProgressBar.jsx. Three more files hand-roll the identical predicate
// instead of importing it (Reveal.jsx, CountUp.jsx, and ShareAction.jsx, which
// inlines the matchMedia call); converging them here is the available follow-up.
export function useScroll() {
  const [metrics, setMetrics] = useState({ y: 0, progress: 0 })

  useEffect(() => {
    let raf = 0
    const update = () => {
      raf = 0
      const y = window.scrollY
      const docH = document.documentElement.scrollHeight - window.innerHeight
      setMetrics({ y, progress: docH > 0 ? Math.min(y / docH, 1) : 0 })
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return metrics
}
